import React, { useEffect, useState } from 'react';
import styled from '@emotion/styled';

import { Gray_Light_White, web_max_width, web_mobile_width } from '@common/styles';
import { hexToRGBA, ScrollLock } from '@common/utils';

export interface DrawerProps {
  open: boolean;
  pure_children?: boolean;
  height?: string;
  maxHeight?: string;
  webHeight?: string;
  overflow?: string;
  onClose: () => void;
  bgColor?: string;
  dimmedBgColor?: string;
  borderRadius?: string;
  zIndex?: number;
}

export const Drawer: React.FC<React.PropsWithChildren<DrawerProps>> = ({
  children,
  open,
  pure_children = false,
  height = '520px',
  overflow = 'hidden',
  maxHeight,
  webHeight,
  onClose,
  dimmedBgColor = '#495057',
  bgColor = Gray_Light_White,
  borderRadius = '8px 8px 0 0',
  zIndex = 999,
}) => {
  const [isContainerVisible, setIsContainerVisible] = useState(false);
  const [isDrawerVisible, setIsDrawerVisible] = useState(false);
  const [containerRef, setContainerRef] = useState<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!containerRef) {
      return;
    }
    const bodyLock = new ScrollLock(containerRef);

    if (open) {
      bodyLock.disableScroll();
      setIsContainerVisible(true);
      setIsDrawerVisible(true);
    } else {
      bodyLock.enableScroll();
      setIsDrawerVisible(false);
    }
    return () => {
      bodyLock.enableScroll();
    };
  }, [open, containerRef]);

  return (
    <Container isVisible={isContainerVisible} zIndex={zIndex}>
      <Dimmed isVisible={isDrawerVisible} dimmedBgColor={dimmedBgColor} onClick={onClose} data-allow-preview />
      <DrawerWrapper
        ref={setContainerRef}
        isVisible={isDrawerVisible}
        $height={height}
        overflow={overflow}
        maxHeight={maxHeight}
        webHeight={webHeight}
        bgColor={bgColor}
        borderRadius={borderRadius}
        onTransitionEnd={() => {
          if (!open) {
            setIsContainerVisible(false);
          }
        }}
      >
        {isContainerVisible && (!pure_children ? <ContentWrapper>{children}</ContentWrapper> : children)}
      </DrawerWrapper>
    </Container>
  );
};

const Container = styled.div<{ isVisible: boolean; zIndex: number }>`
  position: fixed;
  top: 0;
  left: 0;
  z-index: ${({ zIndex }) => zIndex};
  width: 100%;
  height: 100%;
  visibility: ${({ isVisible }) => (isVisible ? 'visible' : 'hidden')};
`;

const Dimmed = styled.div<{ isVisible: boolean; dimmedBgColor: string }>`
  position: absolute;
  top: 0;
  left: 0;
  z-index: 1;
  width: 100%;
  height: 100%;
  opacity: ${({ isVisible }) => (isVisible ? 1 : 0)};
  transition: opacity 0.2s ease;
  background: ${({ dimmedBgColor }) =>
    dimmedBgColor.startsWith('var') ? dimmedBgColor : hexToRGBA(dimmedBgColor, 0.7)};
`;

const DrawerWrapper = styled.div<{
  isVisible: boolean;
  $height: string;
  maxHeight?: string;
  overflow: string;
  webHeight?: string;
  bgColor: string;
  borderRadius?: string;
}>`
  position: absolute;
  left: 50%;
  bottom: 0;
  z-index: 100;
  width: 100%;
  max-width: ${web_max_width}px;
  height: ${({ $height }) => $height};
  max-height: ${({ maxHeight }) => maxHeight || '90%'};
  border-radius: ${({ borderRadius }) => borderRadius};
  overflow: ${({ overflow }) => overflow};
  background-color: ${({ bgColor }) => bgColor};
  transform: translate(-50%, ${({ isVisible }) => (isVisible ? '0' : '100%')});
  transition: transform 0.3s cubic-bezier(0.215, 0.61, 0.355, 1);

  @media screen and (min-width: ${web_mobile_width}px) {
    height: ${({ webHeight, $height }) => webHeight ?? $height};
  }
`;

const ContentWrapper = styled.div`
  height: 100%;
  height: calc(100% - env(safe-area-inset-bottom));
`;
